<script setup lang="ts">
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/solid";
import type { CartItemType, ProductType } from "@/types";
import { ref, computed, watch, onMounted } from "vue";
import dayjs from "dayjs";
import { formatterUtils } from "@/utils/formatterUtils";

// Import formatter and vue utilities
const formatter = formatterUtils();

// Extend the StrapiProductType interface to include the properties we're using
declare module '@/types' {
  interface StrapiProductType {
    detailedPromotionDescription?: string;
  }
}

// Extend the GO Commerce interface to include the properties we're using
declare global {
  interface GOCommerceCustomFields {
    metadataDetailedPromotionDescription?: string;
    metadataBestValue?: string;
    metadataFeatureText?: string;
    isFeaturedProduct?: boolean;
    quotaDataGb?: number;
    validityDuration?: number;
  }
  
  // Add type definitions for discount plan items
  interface DiscountPlanItem {
    customFields?: any;
    [key: string]: any;
  }
}

// Type for the computed properties we'll provide
interface ComputedProperties {
  quotaDataGb: number;
  baseQuotaDataGb: number;
  bonusDataGbNumber: number;
  bonusDataAmount: string | null;
  discountAmount: number;
  finalPrice: string | number;
  hasDiscount: boolean;
  originalPrice: string | null;
  discountedPrice: string | null;
  rppPrice: number;
  planDurationText: string;
  durationUnit: string;
  renewalDuration: number | null;
  isBestValue: boolean;
  featureText: string;
  productFeatures: any[];
  bonusText?: string;
  discountDetails: any;
  [key: string]: any; // Allow other properties
}

type PropsType = {
  product: ProductType;
  cartItem: CartItemType;
  handlePosition: string;
  updateHandlePosition: () => void;
  totalItemsInCart: number;
  updateQuantity: (action: string) => void;
  addToCart: () => void;
  buyNow: () => void;
  clearFields: () => void;
};

const props = defineProps<PropsType>();
console.log(props.product.strapiData?.features);
const {
  currency,
  getProductPrice,
  getProductDiscountValue,
} = formatter;

// Parse HTML content that will come from the API
const parsedPromotionHTML = ref("");

// Computed property to extract detailed promotion description from the API
const detailedPromotionDescription = computed(() => {
  // First check if it exists in Strapi data
  if (props.product?.strapiData?.detailedPromotionDescription) {
    return props.product.strapiData.detailedPromotionDescription;
  }
  
  // Otherwise get from GO Transact API customFields
  return props.product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields?.pim
    ?.goCommerce?.metadataDetailedPromotionDescription || "";
});

// Update the HTML content when component mounts or description changes
onMounted(() => {
  updatePromotionHTML();
});

// Watch for changes to detect if API data updates
watch(detailedPromotionDescription, () => {
  updatePromotionHTML();
});

/**
 * Process HTML content and replace variable placeholders with actual values from product data
 * Placeholder format: {{placeholder}}
 */
function updatePromotionHTML() {
  try {
    if (detailedPromotionDescription.value && detailedPromotionDescription.value.trim()) {
      let htmlContent = detailedPromotionDescription.value;
      console.log("Starting HTML content processing");
      
      // Find all placeholders in the format {{variable}}
      const placeholderRegex = /\{\{([^{}]+)\}\}/g;
      const placeholders = htmlContent.match(placeholderRegex) || [];
      console.log(`Found ${placeholders.length} placeholders:`, placeholders);
      
      // Replace each placeholder
      for (const placeholder of placeholders) {
        const path = placeholder.slice(2, -2).trim(); // Remove {{ and }}
        
        // Handle each specific placeholder
        let replacementValue = '';
        
        switch (path) {
          // Data values
          case 'quotaDataGb':
            replacementValue = String(getQuotaDataGb(props.product));
            break;
          case 'baseQuotaDataGb':
            replacementValue = String(getBaseQuotaDataGb(props.product));
            break;
          case 'bonusDataGbNumber':
            replacementValue = String(getBonusDataGbNumber(props.product));
            break;
          case 'bonusDataAmount':
            replacementValue = getBonusDataAmount(props.product);
            break;
            
          // Price values  
          case 'finalPrice':
            replacementValue = getFinalPrice(props.product);
            break;
          case 'discountAmount':
            replacementValue = getDiscountAmount(props.product);
            break;
          case 'hasDiscount':
            replacementValue = String(hasProductDiscount(props.product));
            break;
          case 'originalPrice':
            replacementValue = getOriginalPrice(props.product);
            break;
          case 'discountedPrice':
            replacementValue = getDiscountedPrice(props.product);
            break;
          case 'rppPrice':
            replacementValue = getRppPrice(props.product);
            break;
            
          // Duration values
          case 'planDurationText':
            replacementValue = getPlanDurationText(props.product);
            break;
          case 'durationUnit':
            replacementValue = getDurationUnit(props.product);
            break;
          case 'renewalDuration':
            replacementValue = getRenewalDuration(props.product);
            break;
            
          // Other values
          case 'isBestValue':
            replacementValue = isProductBestValue(props.product);
            break;
          case 'featureText':
            replacementValue = getFeatureText(props.product);
            break;
          
          // Date formatting helpers
          case 'discountEndDate':
            const endDate = getDiscountDetails(props.product)?.endDate;
            console.log('discountEndDate value:', endDate);
            replacementValue = endDate ? dayjs(endDate).format('DD/MM/YYYY') : '';
            break;
          case 'discountEndDateLong':
            const endDateLong = getDiscountDetails(props.product)?.endDate;
            console.log('discountEndDateLong value:', endDateLong);
            replacementValue = endDateLong ? dayjs(endDateLong).format('D MMMM YYYY') : '';
            break;
          case 'discountStartDate':
            const startDate = getDiscountDetails(props.product)?.startDate;
            console.log('discountStartDate value:', startDate);
            replacementValue = startDate ? dayjs(startDate).format('DD/MM/YYYY') : '';
            break;
          case 'discountEndDateShort':
            const endDateShort = getDiscountDetails(props.product)?.endDate;
            replacementValue = endDateShort ? dayjs(endDateShort).format('DD MMM') : '';
            break;
          case 'discountEndDateLongWithYear':
            const endDateLongWithYear = getDiscountDetails(props.product)?.endDate;
            replacementValue = endDateLongWithYear ? dayjs(endDateLongWithYear).format('D MMMM YYYY') : '';
            break;
        }
        
        // We no longer support complex dayjs expressions directly in templates
        // Instead use our predefined date variables: discountEndDate, discountEndDateLong, etc.
        if (path.includes('dayjs') && path.includes('.format')) {
          console.warn(`Complex dayjs expressions like ${path} are not supported. Use predefined date variables instead.`);
          replacementValue = 'Format not supported';
        }
        
        // If we haven't handled it as a special case, try direct property access
        if (replacementValue === '') {
          try {
            const value = getValueFromPath(props.product, path);
            if (value !== undefined) {
              replacementValue = value.toString();
            }
          } catch (err) {
            console.error(`Error accessing path ${path}:`, err);
          }
        }
        
        console.log(`Replacing ${placeholder} with ${replacementValue}`);  
        htmlContent = htmlContent.replace(placeholder, replacementValue);
      }
      
      parsedPromotionHTML.value = htmlContent;
      console.log("Completed HTML processing with variable replacements");
    } else {
      parsedPromotionHTML.value = "";
      console.log("No HTML content available from API");
    }
  } catch (error) {
    console.error("Error processing HTML:", error);
    parsedPromotionHTML.value = "";
  }
}

/**
 * Extract a value from a nested object using a dot notation path
 * @param {Record<string, any>} obj - The object to extract value from
 * @param {string} path - Dot notation path (e.g., "a.b.c" or "a.0.b")
 * @returns {any} - The value at the specified path or undefined if not found
 */
function getValueFromPath(obj: Record<string, any>, path: string): any {
  try {
    // Handle array indices in the path (convert "x.0.y" to proper traversal)
    return path.split('.').reduce((result: any, key: string) => {
      // If result is null/undefined, return it immediately (avoid errors)
      if (result === null || result === undefined) return result;
      
      // Handle array indices in the path
      if (!isNaN(parseInt(key)) && Array.isArray(result)) {
        return result[parseInt(key)];
      }
      
      // Regular object property access
      return result[key];
    }, obj);
  } catch (error) {
    console.error(`Error accessing path ${path}:`, error);
    return undefined;
  }
}

/**
 * Helper functions for getting computed values similar to Plan/Card.vue
 */

// Data values
function getQuotaDataGb(product: ProductType): string | number {
  const baseDataGb = getBaseQuotaDataGb(product);
  const bonusDataGb = getBonusDataGbNumber(product);
  return baseDataGb + bonusDataGb;
}

function getBaseQuotaDataGb(product: ProductType): number {
  const customFields = product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields;
  return customFields?.pim?.goCommerce?.quotaDataGb || 0;
}

function getBonusDataGbNumber(product: ProductType): number {
  const allowedDiscountPlans = product?.allowedDiscountPlans;
  if (!allowedDiscountPlans || !Array.isArray(allowedDiscountPlans)) {
    return 0;
  }
  
  // Look for bonus data in discount plans
  for (const discountPlan of allowedDiscountPlans) {
    const items = Array.isArray(discountPlan.discountPlanItem) 
      ? discountPlan.discountPlanItem 
      : [discountPlan.discountPlanItem];
      
    for (const item of items) {
      if (item?.customFields?.customField && Array.isArray(item.customFields.customField)) {
        const bonusField = item.customFields.customField.find(
          (f: any) => f.code === "bonusDataAmount" && f.doubleValue
        );
        if (bonusField?.doubleValue) {
          return Math.round(bonusField.doubleValue);
        }
      }
    }
  }
  return 0;
}

function getBonusDataAmount(product: ProductType): string {
  const bonus = getBonusDataGbNumber(product);
  return bonus > 0 ? `${bonus}GB` : "";
}

// Price values
function getFinalPrice(product: ProductType): string {
  const priceObj = getRecurringPriceObject(product);
  let price = priceObj?.price?.taxIncludedAmount || 0;
  
  if (priceObj?.price?.taxIncludedAmountAfterDiscount) {
    price = Number(priceObj.price.taxIncludedAmountAfterDiscount);
  }
  
  if (price === 0) return "$0";
  return price % 1 === 0 ? `$${price}` : formatter.currency(price);
}

function getDiscountAmount(product: ProductType): string {
  const priceObj = getRecurringPriceObject(product);
  let amount = 0;
  
  if (priceObj?.price?.discountAmount) {
    amount = Number(priceObj.price.discountAmount);
  } else if (priceObj?.discount?.discountValue) {
    amount = Number(priceObj.discount.discountValue);
  }
  
  return amount.toString();
}

function hasProductDiscount(product: ProductType): boolean {
  return Number(getDiscountAmount(product)) > 0;
}

function getOriginalPrice(product: ProductType): string {
  if (!hasProductDiscount(product)) return "";
  
  const finalPrice = Number(getFinalPrice(product).replace('$', ''));
  const discountAmount = Number(getDiscountAmount(product));
  const originalPrice = finalPrice + discountAmount;
  
  return formatter.currency(originalPrice);
}

function getDiscountedPrice(product: ProductType): string {
  return hasProductDiscount(product) ? getFinalPrice(product) : "";
}

function getRppPrice(product: ProductType): string {
  const priceObj = getRecurringPriceObject(product);
  const price = priceObj?.price?.taxIncludedAmount || 0;
  return price.toString();
}

// Duration values
function getDurationUnit(product: ProductType): string {
  return product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule?.renewForUnit || "DAY";
}

function getRenewalDuration(product: ProductType): string {
  const duration = product?.offerServiceTemplate?.[0]?.serviceTemplate?.renewalRule?.renewFor;
  return duration?.toString() || "28";
}

function getPlanDurationText(product: ProductType): string {
  const duration = Number(getRenewalDuration(product));
  const unit = getDurationUnit(product);
  
  if (unit === "DAY") {
    return `${duration} days`;
  } else if (unit === "MONTH") {
    return duration > 1 ? `${duration} months` : "month";
  } else if (unit === "YEAR") {
    return duration > 1 ? `${duration} years` : "year";
  }
  
  return "28 days";
}

// Other values
function isProductBestValue(product: ProductType): string {
  const customFields = product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields;
  const isBest = 
    customFields?.pim?.goCommerce?.metadataBestValue === "true" ||
    customFields?.pim?.goCommerce?.isFeaturedProduct === true;
  return isBest ? "true" : "false";
}

function getFeatureText(product: ProductType): string {
  const customFields = product?.offerServiceTemplate?.[0]?.serviceTemplate?.customFields;
  return customFields?.pim?.goCommerce?.metadataFeatureText || "";
}

function getDiscountDetails(product: ProductType): any {
  // For the specific API structure we've seen, the dates are in the allowedDiscountPlans array
  if (product?.allowedDiscountPlans && Array.isArray(product.allowedDiscountPlans) && product.allowedDiscountPlans.length > 0) {
    // Get the first discount plan which should have start/end dates
    const discountPlan = product.allowedDiscountPlans[0];
    if (discountPlan?.startDate || discountPlan?.endDate) {
      console.log('Found discount dates in allowedDiscountPlans:', {
        startDate: discountPlan.startDate,
        endDate: discountPlan.endDate
      });
      return {
        startDate: discountPlan.startDate,
        endDate: discountPlan.endDate,
      };
    }
  }
  
  // Try the old path as fallback
  if (product?.productOfferingPrice?.recurring && Array.isArray(product.productOfferingPrice.recurring)) {
    const priceObj = product.productOfferingPrice.recurring.find(p => p.priceType === "recurring");
    if (priceObj?.discount) {
      console.log('Found discount details from price object:', priceObj.discount);
      return priceObj.discount;
    }
  }
  
  // If we get here, we couldn't find discount details
  console.warn('No discount details found in product, using fallback values');
  return {
    // Provide fallback values based on today's date
    endDate: "2025-04-30T00:00:00Z", // Fixed date matching the API response
    startDate: "2025-04-10T00:00:00Z"  // Fixed date matching the API response
  };
}

// Utility functions
function getRecurringPriceObject(product: ProductType): any {
  if (!product?.productOfferingPrice?.recurring || !Array.isArray(product.productOfferingPrice.recurring)) {
    return null;
  }
  return product.productOfferingPrice.recurring.find(p => p.priceType === "recurring");
}
</script>
<template>
  <div class="product-page container">
    <div class="text-black uppercase text-base font-semibold self-end py-10">
      <NuxtLink to="/">Home</NuxtLink> / Product
    </div>
    <div v-if="product" class="mb-8">
      <div class="flex flex-col md:flex-row md:items-start">
        <div class="w-full md:w-[70%] px-8">
          <!-- Render promotion HTML content directly -->
          <div v-html="parsedPromotionHTML" class="promotion-content"></div>
        </div>
        <div class="w-full md:w-[30%] md:mt-0 mt-4">
          <PlanCard :product="product" :cta-button="true" :buyNow="buyNow" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Styles for the promotional content images */
:deep(.promotion-content) {
  /* Style the three-column icon layout */
  :deep(.flex.justify-between) {
    display: flex;
    justify-content: center;
    gap: 32px; /* Increased spacing between icons */
    margin: 2rem 0;
  }
  
  /* Make the icons larger and centered */
  :deep(.flex.flex-col.items-center) img {
    width: 80px !important; /* 50% larger from original 16px × 3 */
    height: 80px !important;
    margin-bottom: 1rem;
  }
  
  /* Ensure text alignment in the three columns */
  :deep(.flex.flex-col.items-center .text-center) {
    text-align: center;
    width: 100%;
  }
}
</style>
<style scoped lang="scss">
/* Promotion content styling */
.promotion-content-container {
  margin-bottom: 2rem;
}

.promotion-content {
  /* Base styling for the promotion content */
  @apply text-font-primary;
  
  /* Handle heading styles that might be in the HTML */
  h1, h2, h3 {
    @apply text-primary text-[20px] font-[600] mb-4;
  }
  
  p {
    @apply text-font-primary text-[16px] font-[400] mb-4;
  }
  
  /* Style for lists that might be in the HTML */
  ul, ol {
    @apply pl-5 mb-4;
    
    li {
      @apply mb-2;
    }
  }
  
  /* Responsive styles */
  @screen md {
    h1, h2, h3 {
      @apply text-[22px];
    }
    
    p {
      @apply text-[16px];
    }
  }
  
  /* Mobile specific styles */
  @screen sm {
    h1, h2, h3 {
      @apply text-[18px];
    }
    
    p {
      @apply text-[14px];
    }
  }
}

.product-page {
  @apply container flex flex-col bg-background;
  .content {
    @apply flex flex-row justify-center max-md:flex-col;

    .product-detail {
      @apply w-1/2 flex justify-center max-md:w-full max-md:mb-10;
    }
    .add-to-cart {
      @apply w-1/2 flex flex-col items-center max-md:w-full max-md:justify-center my-7;
      @screen md {
        @apply my-7;
      }
    }
  }
}

.slider-container {
  position: relative;
  padding-bottom: 24px;

  .quantity-label {
    position: absolute;
    bottom: 0;
    transform: translateX(-50%);
    font-size: 14px;
    color: rgb(var(--text));
  }
}

:deep(.vue3-slider) {
  width: 100%;
  --height: 6px;
  --color: rgb(var(--primary));
  --track-color: rgb(var(--secondary));
  --tooltip-color: #ffffff;
  --tooltip-text-color: #000000;
  --handle-scale: 1;
  --handle-color: rgb(var(--primary));

  .tooltip {
    transform: translate(0px);
    margin: calc(var(--height, 6px) + 12px);
    bottom: var(--tooltip-margin);
    top: unset;
    display: none;
  }

  .track {
    background-color: rgb(var(--secondary));
  }

  .track-filled {
    background-color: rgb(var(--primary));
  }

  .handle {
    width: 16px;
    height: 16px;
    background-color: rgb(var(--primary));
    border: none;
    box-shadow: none;
    transform: translate(-50%, -40%);
    border-radius: 8px;
  }
}
</style>
